/* eslint sonarjs/cognitive-complexity: 0 */

import React, { useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import { StylesProvider, CssBaseline, createGenerateClassName, MuiThemeProvider } from '@material-ui/core';
import styled, { StyleSheetManager, css } from 'styled-components';
import rtlPlugin from 'stylis-plugin-rtl';
import { Theme } from '@dev/ee-components';
import { SetRtl } from '@api/store/config/actions';
import { jss } from './tools/jss';
import { StylesProps } from './types';
import i18n, { languagesWithRtl } from '@api/i18n';

const overideFontFamilies = {
    ur: css`
        line-height: normal;
    `,
};

const FontFamilyOverride = styled.div<{ $language: string }>`
    ${({ $language }) =>
        overideFontFamilies[$language] &&
        `
        * {
            ${overideFontFamilies[$language]}
        }
    `}
`;

export const Styles = ({ children }: StylesProps) => {
    const dispatch = useDispatch();
    // const { i18n } = useTranslation();
    const isRtlOnHTMLTag = document.getElementsByTagName('html')?.[0]?.getAttribute('dir') === 'rtl' || false;

    const isLocaleRtl = languagesWithRtl?.includes(localStorage.getItem('lng'));
    // const isLocaleRtl = true;

    /**
     * Layout Effect to set trl to themes and styles provider.
     * In the cleanup function grab all styles under jss-insertion-point(where all styles from material sits) and delete them, also deleting google font, as the  nextGenInit() func. loads it again whenever it starts.
     * use it only without standalone
     */

    useLayoutEffect((): any => {
        dispatch(SetRtl(isLocaleRtl));
        document.body.setAttribute('dir', isLocaleRtl || isRtlOnHTMLTag ? 'rtl' : 'ltr');
        (window as any).intercomSettings = {
            ...(window as any).intercomSettings,
            alignment: isLocaleRtl || isRtlOnHTMLTag ? 'left' : 'right',
        };
    }, [isRtlOnHTMLTag, isLocaleRtl]);

    const generateClassName = createGenerateClassName({
        productionPrefix: '@dev/ee-api-',
    });

    const customTheme = {
        ...Theme,
        props: {
            ...Theme.props,
            MuiDialog: {
                disablePortal: true,
            },
        },
        direction: isLocaleRtl || isRtlOnHTMLTag ? 'rtl' : 'ltr',
    };

    const pluginsForStyledComponents = (): any[] => {
        const plugins = [];
        if (isLocaleRtl || isRtlOnHTMLTag) {
            plugins.push(rtlPlugin);
        }
        return plugins;
    };

    return (
        <StyleSheetManager stylisPlugins={pluginsForStyledComponents as any}>
            <StylesProvider injectFirst jss={jss as any} generateClassName={generateClassName}>
                <CssBaseline />
                <FontFamilyOverride $language={i18n.language}>
                    <MuiThemeProvider theme={customTheme}>{children}</MuiThemeProvider>
                </FontFamilyOverride>
            </StylesProvider>
        </StyleSheetManager>
    );
};
